// BookTickets/stages/PaymentProcess/components/MbWayForm/lib/pollTransactionsStatus.ts
type PollTransactionStatusArgs = {
  cartId: string
  intervalMs: number
  maxAttempts: number
  getStatus: () => Promise<string | undefined>
  onTick?: (status: string | undefined, attempt: number) => void
}

export type PollTransactionStatusResult = 'SOLD' | 'OPEN' | 'TIMEOUT' | 'CANCELLED'

type PollTransactionStatusReturn = {
  cancel: () => void
  promise: Promise<PollTransactionStatusResult>
}

/**
 * pollTransactionStatus
 * Small, testable polling helper.
 * Resolves:
 *  - 'SOLD' when successful
 *  - 'OPEN' when failed / reopened
 *  - 'TIMEOUT' after maxAttempts
 *  - 'CANCELLED' if cancelled (unmount, navigation etc)
 */
export function pollTransactionStatus(
  args: PollTransactionStatusArgs,
): PollTransactionStatusReturn {
  const { intervalMs, maxAttempts, getStatus, onTick } = args

  let timer: number | null = null
  let attempt = 0
  let cancelled = false

  const cancel = () => {
    cancelled = true
    if (timer) {
      window.clearInterval(timer)
      timer = null
    }
  }

  const promise = new Promise<PollTransactionStatusResult>((resolve) => {
    timer = window.setInterval(async () => {
      attempt += 1

      if (cancelled) {
        cancel()
        resolve('CANCELLED')
        return
      }

      if (attempt >= maxAttempts) {
        cancel()
        resolve('TIMEOUT')
        return
      }

      try {
        const status = await getStatus()
        onTick?.(status, attempt)

        if (status === 'SOLD') {
          cancel()
          resolve('SOLD')
        } else if (status === 'OPEN') {
          cancel()
          resolve('OPEN')
        } else {
          // RESERVED / unknown -> keep polling
        }
      } catch {
        // transient error -> keep polling
        onTick?.(undefined, attempt)
      }
    }, intervalMs)
  })

  return { cancel, promise }
}
